/**
 * Roles and permissions. This is the single source of truth: the API enforces it
 * on every request, and the web app only uses it to decide what to show.
 */

export const ROLES = ["owner", "admin", "staff", "readonly", "client"] as const;
export type Role = (typeof ROLES)[number];

export const STAFF_ROLES: readonly Role[] = ["owner", "admin", "staff", "readonly"];

export const MODULES = ["tax", "bookkeeping", "payroll", "advisory"] as const;
export type Module = (typeof MODULES)[number];

export const MODULE_LABELS: Record<Module, string> = {
  tax: "Tax",
  bookkeeping: "Accounting Services",
  payroll: "Payroll",
  advisory: "CFO & Advisory",
};

export const PERMISSIONS = [
  "firm.manage",
  "members.manage",
  "audit.read",
  "clients.read",
  "clients.write",
  "clients.archive",
  "templates.manage",
  "engagements.read",
  "engagements.write",
  "tasks.write",
  "requests.write",
  "documents.read",
  "documents.write",
  "messages.read",
  "messages.write",
  "billing.read",
  "billing.write",
  // Record cash/check payments, void invoices: owners and admins only.
  "billing.manage",
  // Refund, hold or apply credit, adjust and void invoices: owners and admins, and staff the owner names
  // in Firm Settings (checked by the API against the firm's list).
  "billing.adjust",
  // Download the whole client list: owners and admins only.
  "clients.export",
  // Firm Analytics (revenue, collections, staff workload): owners and admins only.
  "analytics.read",
  // Firm Settings › Data and security › Backups (the off-site document backup's status): owners and admins only.
  "backups.read",
  // Firm Settings › Integrations: every outside service's status and connection tests, and the firm's own
  // QuickBooks: owners and admins only (connecting the firm's own QuickBooks is the owner's alone).
  "integrations.manage",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const STAFF_WORK: Permission[] = [
  "clients.read",
  "clients.write",
  "engagements.read",
  "engagements.write",
  "tasks.write",
  "requests.write",
  "documents.read",
  "documents.write",
  "messages.read",
  "messages.write",
  "billing.read",
  "billing.write",
];

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: PERMISSIONS,
  admin: PERMISSIONS.filter((p) => p !== "firm.manage"),
  staff: STAFF_WORK,
  readonly: ["clients.read", "engagements.read", "documents.read", "messages.read", "billing.read"],
  // Client users act through the portal endpoints, which scope everything to
  // their own client records. They hold no firm-wide permissions.
  client: [],
};

export function isStaffRole(role: Role | undefined | null): boolean {
  return !!role && STAFF_ROLES.includes(role);
}

export function can(role: Role | undefined | null, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Owners and admins see every module; other staff only the modules assigned to them. */
export function canAccessModule(
  role: Role | undefined | null,
  staffModules: readonly string[] | null | undefined,
  module: Module,
): boolean {
  if (role === "owner" || role === "admin") return true;
  if (role === "staff" || role === "readonly") return (staffModules ?? []).includes(module);
  return false;
}

/** Services with a dashboard in the client portal, which the firm can hide per client. */
export const PORTAL_DASHBOARD_MODULES = ["bookkeeping", "advisory"] as const;
export type PortalDashboardModule = (typeof PORTAL_DASHBOARD_MODULES)[number];

/**
 * A service's dashboard (its overview page) is shown to everyone who works in the service,
 * unless the owner has hidden it from that person. Owners and admins always see it.
 */
export function canSeeDashboard(
  role: Role | undefined | null,
  staffModules: readonly string[] | null | undefined,
  hiddenDashboards: readonly string[] | null | undefined,
  module: Module,
): boolean {
  if (!canAccessModule(role, staffModules, module)) return false;
  if (role === "owner" || role === "admin") return true;
  return !(hiddenDashboards ?? []).includes(module);
}
